import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  CircularProgress,
  Typography,
  Stack,
  IconButton
} from '@mui/material';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import WebViewer from '@pdftron/webviewer';
import { supabase } from 'src/supabase/supabaseClient';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';

/**
 * Advanced PDF Editor using Apryse WebViewer
 * Supports direct PDF editing, annotations, and persistence to Supabase.
 */
const PDFEditor = ({ open, onClose, pdfUrl, attachmentId, requestId, documentSource = 'request', onSaveSuccess }) => {
  const viewer = useRef(null);
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const lastLoadedUrl = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [initError, setInitError] = useState(null);

  // Use a callback ref to handle potential timing issues with the ref availability
  const viewerRef = (el) => {
    if (el && !viewer.current) {
      viewer.current = el;
      console.log('✅ [PDFEditor] Viewer element assigned');
      // Trigger initialization if not already done
      if (open && !instance) {
        initWebViewer();
      }
    }
  };

  const initWebViewer = async () => {
    if (!viewer.current || instance || !open) return;

    try {
      setLoading(true);
      setInitError(null);
      console.log('🚀 [PDFEditor] initWebViewer starting for:', pdfUrl);
      console.log('📦 [PDFEditor] WebViewer type:', typeof WebViewer);

      if (!pdfUrl) {
        console.warn('⚠️ [PDFEditor] No PDF URL provided.');
        return;
      }

      // Handle both ESM and CommonJS imports
      const WebViewerConstructor = WebViewer.default || WebViewer;

      const inst = await WebViewerConstructor(
        {
          licenseKey: 'YOUR_LICENSE_KEY_HERE', // 🔑 ADD YOUR LICENSE KEY HERE
          path: '/static/webviewer',
          initialDoc: pdfUrl,
          fullAPI: true,
          enableFilePicker: false,
          preloadWorker: 'pdf',
          extension: 'pdf',
          disabledElements: []
        },
        viewer.current
      );

      console.log('✅ [PDFEditor] WebViewer instance created');
      setInstance(inst);
      lastLoadedUrl.current = pdfUrl;

      // Robust access to WebViewer API (Handles variants across v8 versions)
      const Core = inst.Core || inst.current?.Core;
      const UI = inst.UI || inst.current?.UI;
      
      if (UI) {
        try {
          // 1. Set Dark Theme
          if (typeof UI.setTheme === 'function') {
            UI.setTheme('dark');
          }

          const Feature = UI.Feature || (Core && Core.Feature) || inst.Feature;
          if (Feature) {
            // 2. Enable ALL Advanced Features
            const allFeatures = [
              Feature.ContentEdit,
              Feature.Signature,
              Feature.Measurement,
              Feature.FilePicker,
              Feature.MultiTab,
              Feature.NotesPanel,
              Feature.Redaction,
              Feature.ContentDirectEdit
            ];
            
            // Filter out any that might be undefined in this specific version
            const validFeatures = allFeatures.filter(f => f !== undefined);
            UI.enableFeatures(validFeatures);
            
            // Re-enable Print and Download if they were disabled
            UI.enableFeatures([Feature.Print, Feature.Download]);
          }

          // 3. Ensure Header is fully enabled
          if (typeof UI.enableElements === 'function') {
            UI.enableElements(['header', 'topHeader', 'toolsHeader']);
          }

        } catch (uiErr) {
          console.warn('⚠️ [PDFEditor] UI customization failed:', uiErr);
        }
      }

      const docViewer = (Core && Core.documentViewer) || (inst.Core && inst.Core.documentViewer);
      if (docViewer) {
        docViewer.addEventListener('documentLoaded', () => {
          console.log('✅ [PDFEditor] PDF Document loaded successfully');
          setLoading(false);
        });

        docViewer.addEventListener('loaderror', (err) => {
          console.error('❌ [PDFEditor] PDF Load Error:', err);
          setLoading(false);
          enqueueSnackbar('Error loading PDF document', { variant: 'error' });
        });
      } else {
        console.warn('⚠️ [PDFEditor] documentViewer not found. Trying to wait for instance state...');
        // Sometimes it takes a moment to populate or uses a different event
        setLoading(false); 
      }

    } catch (err) {
      console.error('❌ [PDFEditor] WebViewer initialization failed:', err);
      setLoading(false);
      setInitError(err.message || 'Initialization failed');
      enqueueSnackbar('Failed to load PDF Editor: ' + (err.message || 'Check logs'), { variant: 'error' });
    }
  };

  useEffect(() => {
    if (open && pdfUrl) {
      if (viewer.current && !instance) {
        initWebViewer();
      }
    }
  }, [open, pdfUrl, instance]);

  useEffect(() => {
    if (instance && pdfUrl && pdfUrl !== lastLoadedUrl.current) {
      // If instance exists but URL changed, load the new document
      setLoading(true);
      console.log('🔄 [PDFEditor] Loading new document:', pdfUrl);
      instance.UI.loadDocument(pdfUrl);
      lastLoadedUrl.current = pdfUrl;
    }
  }, [instance, pdfUrl]);

  const handleSave = async () => {
    // 1. Better availability check
    if (!instance || !attachmentId) {
      const missing = !instance ? 'Editor Instance' : 'Attachment ID';
      console.error(`❌ [PDFEditor] Cannot save: Missing ${missing}`, { attachmentId, requestId });
      enqueueSnackbar(`Cannot save: Missing ${missing}`, { variant: 'error' });
      return;
    }

    try {
      setSaving(true);
      
      // Robust API access (handles different v8 versions)
      const Core = instance.Core || instance.current?.Core;
      const UI = instance.UI || instance.current?.UI;
      
      if (!Core) {
        throw new Error('PDF Editor Core API not available');
      }

      const docViewer = Core.documentViewer;
      const annotationManager = Core.annotationManager || (typeof docViewer?.getAnnotationManager === 'function' ? docViewer.getAnnotationManager() : null);

      if (!docViewer || !annotationManager) {
        throw new Error('PDF Viewer tools (documentViewer/annotationManager) not ready');
      }

      console.log('⏳ [PDFEditor] Preparing PDF for save...', { attachmentId, requestId });
      
      const doc = docViewer.getDocument();
      const xfdfString = await annotationManager.exportAnnotations();
      const data = await doc.getFileData({ xfdfString });
      const blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' });

      const fileName = `edited_${attachmentId}_${Date.now()}.pdf`;
      const filePath = `${documentSource}-attachments/${fileName}`;
      
      console.log(`📤 [PDFEditor] Uploading ${blob.size} bytes to Supabase: ${filePath}`);

      // Upload to Supabase
      const { error: uploadError } = await supabase.storage
        .from('signwise-attachments')
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('signwise-attachments')
        .getPublicUrl(filePath);

      console.log('✅ [PDFEditor] Uploaded. Public URL:', publicUrl);

      const finalAttachmentId = Number(attachmentId);
      const finalRequestId = Number(requestId || 0);

      if (isNaN(finalAttachmentId)) {
        throw new Error('Invalid Attachment ID');
      }

      const apiUrl = `${REST_API_END_POINT}request/save-edited-pdf-file`;
      console.log('📡 [PDFEditor] Updating database via backend...', { 
        attachmentId: finalAttachmentId, 
        requestId: finalRequestId,
        publicUrl,
        endpoint: apiUrl
      });

      // Using global axios to ensure standard behavior if custom instance is causing issues
      const response = await axios.post(apiUrl, {
        attachmentId: finalAttachmentId,
        requestId: finalRequestId,
        documentSource,
        supabaseUrl: publicUrl,
        fileSize: blob.size
      });

      if (response && response.data && response.data.status === 1) {
        console.log('🎉 [PDFEditor] Save completed successfully');
        enqueueSnackbar('Changes saved successfully', { variant: 'success' });
        if (onSaveSuccess) onSaveSuccess();
        onClose();
      } else {
        const backendMsg = response?.data?.message || 'Database update failed';
        console.error('❌ [PDFEditor] Backend error:', backendMsg);
        throw new Error(backendMsg);
      }

    } catch (error) {
      console.error('❌ [PDFEditor] Save process aborted:', error);
      
      let errorMsg = 'An unexpected error occurred while saving';
      if (typeof error === 'string') {
        errorMsg = error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      enqueueSnackbar('Error saving PDF: ' + errorMsg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      fullScreen 
      open={open} 
      onClose={onClose}
    >
      <DialogTitle 
        sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #333',
          backgroundColor: '#1e1e1e',
          color: '#fff'
        }}
      >
        <Typography variant="h6" component="span">Advanced PDF Editor</Typography>
        
        <Stack direction="row" spacing={2} alignItems="center">
          <Button 
            variant="contained" 
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Icon icon={saveFill} />}
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ fontWeight: 'bold' }}
          >
            {saving ? 'Saving...' : 'Save & Close'}
          </Button>
          
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ backgroundColor: 'rgba(255,255,255,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
          >
            <Icon icon={closeFill} />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, overflow: 'hidden', height: '100%', backgroundColor: '#121212', position: 'relative' }}>
        {loading && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              backgroundColor: '#121212',
              zIndex: 100, // Very high z-index
              gap: 2
            }}
          >
            <CircularProgress color="primary" />
            <Typography variant="body1" color="textSecondary">Initializing PDF Editor...</Typography>
            {initError && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                Error: {initError}
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary" sx={{ mt: 2 }}>
              Target URL: {pdfUrl || 'None'}
            </Typography>
          </Box>
        )}
        <div 
          ref={viewerRef} 
          style={{ 
            height: '100%', 
            width: '100%'
          }} 
        />
      </DialogContent>
    </Dialog>
  );
};

export default PDFEditor;
